import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Localized, Locale } from "../../shared/types";
import { messages, type MessageKey } from "./messages";

export const DEFAULT_LOCALE: Locale = "es";
export const LOCALES: readonly Locale[] = ["es", "en"];
export const STORAGE_KEY = "pokefans.locale";

type Vars = Record<string, string | number>;

export interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  /** Translates a UI key. Uses `<key>_one` when `vars.count === 1` and that key exists. */
  t: (key: MessageKey, vars?: Vars) => string;
  /** Picks the current-locale value of DB-localized content, falling back to Spanish. */
  pick: (value: Localized) => string;
}

const I18nContext = createContext<I18nValue | null>(null);

export function isLocale(value: unknown): value is Locale {
  return value === "es" || value === "en";
}

function readStoredLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => (name in vars ? String(vars[name]) : match));
}

export function translate(locale: Locale, key: MessageKey, vars?: Vars): string {
  const table = messages[locale] as Record<string, string>;
  const pluralKey = `${key}_one`;
  const template = vars?.count === 1 && pluralKey in table ? table[pluralKey] : table[key] ?? messages.es[key] ?? key;
  return interpolate(template, vars);
}

export function I18nProvider({ children, initialLocale }: { children: ReactNode; initialLocale?: Locale }) {
  const [locale, setLocaleState] = useState<Locale>(() => initialLocale ?? readStoredLocale());

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Storage may be unavailable (private mode); the choice still applies for this session.
    }
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (key, vars) => translate(locale, key, vars),
      pick: (v) => v[locale] || v.es,
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}
